<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'

/**
 * PModal — dialogue centré pour les formulaires longs (create user,
 * configure route, etc.). Pour les confirmations simples, préférer PDialog
 * qui expose une API confirm/cancel sans avoir à câbler les boutons.
 *
 * Pattern v-model:open : le parent contrôle l'affichage. Le composant
 * gère la fermeture sur ESC + click backdrop, le `body-overflow: hidden`
 * pendant l'ouverture, et le teleport vers `body` pour échapper aux z-index.
 *
 * Accessibilité : `role="dialog"`, `aria-modal="true"`, `aria-labelledby`.
 */
interface Props {
    open: boolean
    title?: string
    size?: 'sm' | 'md' | 'lg'
    /** Si true, click backdrop + ESC ne ferment pas (force le user à choisir). */
    persistent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    persistent: false,
})

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'close'): void
}>()

function close() {
    emit('update:open', false)
    emit('close')
}

function onBackdropClick() {
    if (!props.persistent) {
        close()
    }
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.open && !props.persistent) {
        close()
    }
}

watch(
    () => props.open,
    (isOpen) => {
        if (typeof document === 'undefined') return
        document.body.style.overflow = isOpen ? 'hidden' : ''
    },
)

onMounted(() => {
    document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
    }
})
</script>

<template>
    <Teleport to="body">
        <Transition name="fade">
            <div
                v-if="open"
                class="p-modal-backdrop"
                role="presentation"
                @click.self="onBackdropClick"
            >
                <Transition name="dialog" appear>
                    <div
                        v-if="open"
                        :class="['p-modal', `p-modal--${size}`]"
                        role="dialog"
                        aria-modal="true"
                        :aria-labelledby="title ? 'p-modal-title' : undefined"
                    >
                        <header v-if="title || $slots.header" class="p-modal__header">
                            <slot name="header">
                                <h2 id="p-modal-title" class="p-modal__title">{{ title }}</h2>
                            </slot>
                            <button
                                v-if="!persistent"
                                type="button"
                                class="p-modal__close"
                                :aria-label="$t('common.close')"
                                @click="close"
                            >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                                    <path d="M3 3l10 10M13 3L3 13" />
                                </svg>
                            </button>
                        </header>
                        <div class="p-modal__body">
                            <slot />
                        </div>
                        <footer v-if="$slots.footer" class="p-modal__footer">
                            <slot name="footer" />
                        </footer>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.p-modal-backdrop {
    position: fixed;
    inset: 0;
    background: var(--p-color-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--p-space-4);
    z-index: 1000;
}

.p-modal {
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    box-shadow: var(--p-shadow-modal);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - var(--p-space-8));
    width: 100%;
}

.p-modal--sm {
    max-width: 400px;
}
.p-modal--md {
    max-width: 560px;
}
.p-modal--lg {
    max-width: 840px;
}

.p-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--p-color-line);
}

.p-modal__title {
    margin: 0;
    font-size: var(--p-text-base);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}

.p-modal__close {
    background: transparent;
    border: none;
    color: var(--p-color-ink-4);
    cursor: pointer;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--p-radius-sm);
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}

.p-modal__close:hover {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}

.p-modal__body {
    padding: 20px;
    overflow-y: auto;
}

.p-modal__footer {
    padding: 12px 20px;
    border-top: 1px solid var(--p-color-line);
    display: flex;
    gap: var(--p-space-3);
    justify-content: flex-end;
}
</style>
